import type { FormModel, FormModelField } from '@ikegps/core-shared';
import { Icon } from '../../components/icon';
import { MaterialIcon } from '../../components/icon/icons';
import { Spinner } from '../../components/spinner/spinner';
import { T, Translate } from '../../i18n';
import { m, type MithrilVNode } from '../../mithril';
import { classNames } from '../../util/classnames';
import { FormDesignerPalette } from '../form.designer/form.palette';
import type { DropdownFieldLoss, DropdownLostValue } from './collection.form.migrate.dropdown.loss';
import type { MapFieldsState } from './collection.form.migrate.map.fields.state';
import { fieldGroupPath, fieldLabel } from './collection.form.migrate.map.fields.util';
import { ReviewState, type ReviewLossTab } from './collection.form.migrate.review.state';
import { CSS } from './collection.form.migrate.review.style';

export interface ReviewViewProps {
	sourceForm: FormModel;
	targetForm: FormModel;
	reviewState: ReviewState;
}

function view(props: ReviewViewProps): MithrilVNode {
	const { sourceForm, targetForm, reviewState } = props;
	const summary = reviewState.summary;

	return m('div', { className: CSS.root }, [
		m('div', { className: CSS.heading }, Translate(T.COLLECTION_MIGRATE_FORM_REVIEW_HEADING)),
		m('div', { className: CSS.breadcrumb }, [
			m('span', { className: CSS.metaBold }, sourceForm.displayName),
			m('span', { className: CSS.metaMuted }, ' → '),
			m('span', { className: CSS.metaBold }, `${targetForm.displayName} (V${targetForm.displayVersion})`),
			m('span', { className: CSS.metaMuted }, ' • '),
			m(
				'span',
				{ className: CSS.metaBold },
				Translate(T.COLLECTION_MIGRATE_FORM_POLES_SELECTED, { count: summary.collectionsImpacted })
			),
		]),
		m('div', { className: CSS.cards }, [
			viewCard(summary.collectionsImpacted, T.COLLECTION_MIGRATE_FORM_CARD_COLLECTIONS_IMPACTED, false),
			viewCard(summary.fieldsMapped, T.COLLECTION_MIGRATE_FORM_CARD_FIELDS_MAPPED, false),
			viewCard(summary.fieldsLost.length, T.COLLECTION_MIGRATE_FORM_CARD_FIELDS_LOST, summary.fieldsLost.length > 0),
			viewCard(
				summary.dropdownValuesLost,
				T.COLLECTION_MIGRATE_FORM_CARD_DROPDOWN_VALUES_LOST,
				(summary.dropdownValuesLost ?? 0) > 0
			),
		]),
		viewLossPanel(reviewState, summary.fieldsLost),
		m('div', { className: CSS.footerNote }, [
			Icon.view(MaterialIcon.MANAGE_HISTORY, { className: CSS.footerNoteIcon }),
			m('span', {}, Translate(T.COLLECTION_MIGRATE_FORM_REVIEW_FOOTER_NOTE)),
		]),
	]);
}

function viewCard(value: number | null, labelToken: T, warn: boolean): MithrilVNode {
	return m('div', { className: CSS.card }, [
		m(
			'div',
			{ className: `${CSS.cardValue} ${warn ? CSS.cardValueWarn : ''}` },
			value == null ? Spinner.view(CSS.cardSpinner) : `${value}`
		),
		m('div', { className: CSS.cardLabel }, Translate(labelToken)),
	]);
}

function viewLostRow(field: FormModelField, mapFieldsState: MapFieldsState): MithrilVNode {
	const collectionsImpacted = mapFieldsState.getCollectionsImpactedFor(field);

	return m('div', { className: CSS.lostRow }, [
		FormDesignerPalette.viewIcon(field.type, { className: CSS.lostIcon }),
		m('div', { className: CSS.lostText }, [
			m('div', { className: CSS.lostName }, fieldLabel(field)),
			m('div', { className: CSS.lostMeta }, `${fieldGroupPath(field)} · ${FormDesignerPalette.translate(field.type)}`),
		]),
		m(
			'div',
			{ className: CSS.lostRowCount },
			collectionsImpacted == null
				? Spinner.view(CSS.lostRowSpinner)
				: Translate(T.COLLECTION_MIGRATE_FORM_POLES_IMPACTED_COUNT, { count: collectionsImpacted })
		),
	]);
}

function viewLossPanel(reviewState: ReviewState, fieldsLost: FormModelField[]): MithrilVNode {
	return m('div', { className: CSS.lostPanel }, [
		viewLossTabBar(reviewState),
		reviewState.activeLossTab === 'fieldsLost'
			? viewFieldsLostContent(fieldsLost, reviewState.mapFieldsState)
			: viewDropdownLossContent(reviewState.mapFieldsState.dropdownFieldsLost),
	]);
}

function viewLossTabBar(reviewState: ReviewState): MithrilVNode {
	return m('div', { className: CSS.tabBar }, [
		viewLossTab(reviewState, 'fieldsLost', T.COLLECTION_MIGRATE_FORM_FIELDS_LOST_HEADING),
		viewLossTab(reviewState, 'dropdownLoss', T.COLLECTION_MIGRATE_FORM_DROPDOWN_LOST_HEADING),
	]);
}

function viewLossTab(reviewState: ReviewState, tab: ReviewLossTab, labelToken: T): MithrilVNode {
	return m(
		'div',
		{
			className: classNames(CSS.tab, { [CSS.tabActive]: reviewState.activeLossTab === tab }),
			onclick: () => reviewState.setActiveLossTab(tab),
		},
		Translate(labelToken)
	);
}

function viewFieldsLostContent(fieldsLost: FormModelField[], mapFieldsState: MapFieldsState): MithrilVNode {
	return fieldsLost.length > 0
		? m(
				'div',
				{ className: CSS.lossTabScroll },
				fieldsLost.map((field) => viewLostRow(field, mapFieldsState))
			)
		: m('div', { className: CSS.lostEmpty }, Translate(T.COLLECTION_MIGRATE_FORM_FIELDS_LOST_EMPTY));
}

function viewDropdownLossContent(fieldsLost: DropdownFieldLoss[]): MithrilVNode {
	return fieldsLost.length > 0
		? m('div', { className: CSS.lossTabScroll }, fieldsLost.map(viewDropdownLossRow))
		: m('div', { className: CSS.lostEmpty }, Translate(T.COLLECTION_MIGRATE_FORM_DROPDOWN_LOST_EMPTY));
}

function viewDropdownLossRow(loss: DropdownFieldLoss): MithrilVNode {
	const { field, distinctValues } = loss;

	return m('details', { className: CSS.dropdownLossRow }, [
		m('summary', { className: CSS.dropdownLossRowHeader }, [
			Icon.view(MaterialIcon.KEY_RIGHT, { className: CSS.dropdownLossChevron }),
			FormDesignerPalette.viewIcon(field.type, { className: CSS.lostIcon }),
			m('div', { className: CSS.lostText }, [
				m('div', { className: CSS.lostName }, fieldLabel(field)),
				m('div', { className: CSS.lostMeta }, fieldGroupPath(field)),
			]),
			m(
				'div',
				{ className: CSS.dropdownLossCount },
				Translate(T.COLLECTION_MIGRATE_FORM_DROPDOWN_LOST_FIELD_COUNT, { count: distinctValues.length })
			),
		]),
		m('div', { className: CSS.dropdownLossValues }, distinctValues.map(viewDropdownLossValue)),
	]);
}

function viewDropdownLossValue(lostValue: DropdownLostValue): MithrilVNode {
	return m('div', { className: CSS.dropdownLossValue }, [
		m('div', { className: CSS.dropdownLossValueLabel }, lostValue.value),
		m(
			'div',
			{ className: CSS.dropdownLossValueCollections },
			Translate(T.COLLECTION_MIGRATE_FORM_POLES_IMPACTED_COUNT, { count: lostValue.collectionCount })
		),
	]);
}

export const ReviewView = { view };
